import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ScoreBadge } from '../components/Badges';
import {
  ArrowLeft, Mail, Phone, Calendar, Crown, Target, CheckCircle2, AlertCircle,
  TrendingUp, Users, Euro, Activity, ExternalLink, ChevronRight, CalendarRange, X,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from 'recharts';
import { fmtRelative, SOURCE_LABELS } from '../lib/constants';
import { logger } from '../lib/logger';

const fmtEur = (n) => {
  const num = Number(n) || 0;
  if (num === 0) return '€ 0';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(num);
};

const TICK_STYLE = { fontSize: 10, fill: '#6b7280' };

// ★ 2026-10-05 (Fase 41): label italiane + ordine per i 14 stage della pipeline.
// Sync con /app/backend/pipeline_v2.py (STAGE_ORDER + STAGE_DEFS.label).
// STAGE_LABELS è ordered — Object.entries mantiene l'ordine di inserimento.
const STAGE_LABELS = {
  lead: '🆕 Lead',
  chiamato: '📞 Chiamato',
  da_richiamare: '🔁 Da richiamare',
  risposta: '💬 Risposta',
  appuntamento_fissato: '📆 Appuntamento',
  show: '🎤 Show',
  pending_show: '⏸️ Pending',
  no_show: '⛔ No-show',
  verification: '🛡️ Verification',
  proposta_inviata: '📄 Proposta inviata',
  proposta_ricevuta: '📥 Proposta ricevuta',
  chiuso_cliente: '🏆 Chiuso Cliente',
  chiuso_partner: '🤝 Chiuso Partner',
  perso: '❌ Perso',
  // Legacy alias (retrocompatibilità: lead vecchi con stage 'chiuso' semplice)
  chiuso: '🏆 Chiuso',
};

const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function AgentDetailPage() {
  const { agentId } = useParams();
  const nav = useNavigate();
  const [days, setDays] = useState('30');
  // ★ 2026-07-10: date range personalizzabile (opzionale — override di `days`)
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [useDateRange, setUseDateRange] = useState(false);
  const [kpi, setKpi] = useState(null);
  const [agent, setAgent] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsExpanded, setLeadsExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  // ★ 2026-07-14 (richiesta utente): filtro pipeline (stage) nella lista lead del portfolio agente
  const [stageFilter, setStageFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (useDateRange && startDate && endDate) {
      params.append('start_date', startDate);
      params.append('end_date', endDate);
    } else {
      params.append('days', days);
    }
    // ★ 2026-07-15 (bug fix): la lista lead ora rispetta anche il date range
    // custom (prima usava solo agent_id → lista non si aggiornava sui cambi periodo).
    // Usiamo date_field=activity per includere anche i lead che sono PASSATI
    // per uno stage nel periodo (allineamento con KPI event-date based).
    const leadsParams = new URLSearchParams();
    leadsParams.append('agent_id', agentId);
    leadsParams.append('limit', '2000');
    leadsParams.append('date_field', 'activity');
    if (useDateRange && startDate && endDate) {
      leadsParams.append('start_date', startDate);
      leadsParams.append('end_date', endDate);
    } else {
      // Convert `days` in un range esplicito così la lista è coerente col KPI
      const now = new Date();
      const from = new Date();
      from.setDate(from.getDate() - parseInt(days, 10));
      leadsParams.append('start_date', from.toISOString().slice(0, 10));
      leadsParams.append('end_date', now.toISOString().slice(0, 10));
    }
    Promise.all([
      api.get(`/kpis/agent/${agentId}?${params.toString()}`).then((r) => r.data),
      api.get('/agents').then((r) => r.data),
      api.get(`/leads?${leadsParams.toString()}`).then((r) => r.data),
    ]).then(([k, allAgents, lr]) => {
      if (cancelled) return;
      setKpi(k);
      const found = (allAgents || []).find((a) => a.id === agentId);
      setAgent(found || k.agent);
      setLeads(lr || []);
    }).catch((err) => {
      logger.error('Agent detail load failed:', err);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [agentId, days, useDateRange, startDate, endDate]);

  // Handler per attivare quick range presets
  const applyQuickRange = (n) => {
    setStartDate(daysAgoIso(n));
    setEndDate(todayIso());
    setUseDateRange(true);
  };

  const resetDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setUseDateRange(false);
  };

  if (loading || !kpi) {
    return <div className="p-8 text-muted-foreground">Caricamento KPI agente…</div>;
  }

  const isAssociate = kpi.agent?.is_associate_manager;
  const isSenior = kpi.agent?.is_senior;

  const stageBarData = (kpi.stages || []).map((s) => ({
    stage: s.label,
    count: s.count || 0,
    color: s.color,
  }));

  // ★ 2026-07-14: filtro pipeline lato client per la lista portfolio
  const leadsByStage = leads.reduce((acc, l) => {
    const s = l.stage || 'lead';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const filteredLeads = stageFilter === 'all'
    ? leads
    : leads.filter((l) => (l.stage || 'lead') === stageFilter);

  return (
    <div className="p-4 md:p-6 space-y-4 fade-up">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="agent-detail-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
        </Button>
        <div className="flex-1 flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-[hsl(var(--primary))] text-white grid place-items-center font-serif text-xl shrink-0">
            {(kpi.agent?.name || '?').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h1 className="font-serif text-2xl text-[hsl(var(--primary))] truncate" data-testid="agent-detail-name">
              {kpi.agent?.name}
            </h1>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <Badge variant="secondary" className="text-[10px]">
                {kpi.agent?.role === 'ceo' && <Crown className="w-3 h-3 mr-1 inline" />}
                {kpi.agent?.role || 'agent'}
              </Badge>
              {isAssociate && <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-300">Associate</Badge>}
              {isSenior && <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-300">Senior</Badge>}
            </div>
          </div>
        </div>
        <Select
          value={useDateRange ? 'custom' : days}
          onValueChange={(v) => {
            if (v === 'custom') {
              setUseDateRange(true);
              if (!startDate) setStartDate(daysAgoIso(30));
              if (!endDate) setEndDate(todayIso());
            } else {
              setUseDateRange(false);
              setDays(v);
            }
          }}
        >
          <SelectTrigger className="w-44" data-testid="agent-period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Ultimi 7 giorni</SelectItem>
            <SelectItem value="30">Ultimi 30 giorni</SelectItem>
            <SelectItem value="90">Ultimi 90 giorni</SelectItem>
            <SelectItem value="365">Ultimi 12 mesi</SelectItem>
            <SelectItem value="custom">📅 Personalizza date…</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* ★ 2026-07-10: Date range personalizzata */}
      {useDateRange && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <CalendarRange className="w-4 h-4 text-blue-700" />
            <Label className="text-xs text-blue-900 font-semibold">Range personalizzato:</Label>
            <Label className="text-xs text-muted-foreground">Da</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-8 w-36 text-xs bg-white"
              data-testid="agent-start-date"
            />
            <Label className="text-xs text-muted-foreground">a</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-8 w-36 text-xs bg-white"
              data-testid="agent-end-date"
            />
            <div className="flex gap-1 ml-1">
              {[
                { label: '7g', d: 7 },
                { label: '30g', d: 30 },
                { label: '90g', d: 90 },
              ].map((q) => (
                <Button
                  key={q.d}
                  size="sm"
                  variant="ghost"
                  className="h-7 text-[11px] px-2"
                  onClick={() => applyQuickRange(q.d)}
                  data-testid={`agent-quick-${q.d}d`}
                >
                  Ultimi {q.label}
                </Button>
              ))}
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-[11px] ml-auto"
              onClick={resetDateFilter}
              data-testid="agent-reset-date"
            >
              <X className="w-3 h-3 mr-1" /> Reset
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Contact details */}
      <Card className="border-border">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-muted-foreground" /> {kpi.agent?.email}</span>
            {agent?.phone && (
              <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-muted-foreground" /> {agent.phone}</span>
            )}
            {agent?.calendly_url && (
              <a href={agent.calendly_url} target="_blank" rel="noopener noreferrer"
                 className="text-[var(--gp-gold)] hover:underline inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> Calendly <ExternalLink className="w-2.5 h-2.5" />
              </a>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Top-line KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          label="Lead totali"
          value={kpi.total_leads}
          icon={Users}
          color="emerald"
        />
        <KpiTile
          label="Come Associate"
          value={kpi.associate_total}
          icon={TrendingUp}
          color="cyan"
        />
        <KpiTile
          label="Come Senior"
          value={kpi.senior_total}
          icon={Activity}
          color="amber"
        />
        <KpiTile
          label="Revenue chiusi"
          value={fmtEur(kpi.revenue_closed_eur)}
          icon={Euro}
          color="green"
          isString
        />
      </div>

      {/* Conversion-rate target cards
          ★ 2026-08-02 v2 — la KPI target per Associate è ORA Risposta → Show
          (target 45%). La vecchia Lead → Show è stata rimossa come target. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {isAssociate && (
          <ConversionCard
            kind="associate"
            kpi={kpi.associate_kpi}
            title="Conversion Associate: Risposta → Show"
            description={`${kpi.associate_kpi?.shows || 0} / ${kpi.associate_kpi?.responses || 0} lead che hanno risposto sono arrivati allo Show`}
          />
        )}
        {isSenior && (
          <ConversionCard
            kind="senior"
            kpi={kpi.senior_kpi}
            title="Conversion Senior: Show → Chiuso"
            description={`${kpi.senior_kpi?.closes || 0} / ${kpi.senior_kpi?.shows || 0} chiusi dai show`}
          />
        )}
        {!isAssociate && !isSenior && (
          <Card className="border-dashed">
            <CardContent className="p-4 text-sm text-muted-foreground">
              Questo agente non è né Associate né Senior — nessun target di conversione applicabile.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Pipeline distribution chart */}
      <Card className="border-border">
        <CardContent className="p-4">
          <div className="mb-3">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Distribuzione</div>
            <div className="font-medium">Lead per stage pipeline</div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stageBarData}>
                <CartesianGrid stroke="#EEF2F0" />
                <XAxis dataKey="stage" tick={TICK_STYLE} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={TICK_STYLE} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {stageBarData.map((entry, i) => (
                    <Cell key={`c-${i}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Lead list */}
      <Card className="border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Portfolio lead</div>
              <div className="font-medium">
                <span data-testid="agent-leads-total">{leads.length}</span> lead totali
                {stageFilter !== 'all' && (
                  <span className="text-xs ml-2 text-blue-700 font-normal">
                    · filtrati per <b>{STAGE_LABELS[stageFilter] || stageFilter}</b>: <span data-testid="agent-leads-filtered-count">{filteredLeads.length}</span>
                  </span>
                )}
                {stageFilter === 'all' && leads.length > 20 && !leadsExpanded && (
                  <span className="text-xs text-muted-foreground ml-1">
                    · mostrati primi 20
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger className="w-52 h-9" data-testid="agent-leads-stage-filter">
                  <SelectValue placeholder="Fase pipeline" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    Tutte le fasi ({leads.length})
                  </SelectItem>
                  {Object.entries(STAGE_LABELS).map(([key, label]) => {
                    const count = leadsByStage[key] || 0;
                    return (
                      <SelectItem key={key} value={key} disabled={count === 0}>
                        {label} ({count})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {stageFilter !== 'all' && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-9 px-2 text-xs"
                  onClick={() => setStageFilter('all')}
                  data-testid="agent-leads-stage-filter-reset"
                >
                  <X className="w-3 h-3 mr-1" /> Reset
                </Button>
              )}
              {filteredLeads.length > 20 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLeadsExpanded((v) => !v)}
                  data-testid="agent-leads-toggle-expand"
                >
                  {leadsExpanded ? `Comprimi (mostra i primi 20)` : `Mostra tutti (${filteredLeads.length})`}
                </Button>
              )}
              <Link to={`/app/leads?agent_id=${agentId}${stageFilter !== 'all' ? `&stage=${stageFilter}` : ''}`}>
                <Button variant="outline" size="sm" data-testid="agent-view-in-leads-page">
                  Apri nella lista Lead
                </Button>
              </Link>
            </div>
          </div>

          {/* ★ 2026-07-14: Quick pills — cliccabili per filtrare per stage */}
          <div className="flex flex-wrap gap-1.5 mb-3 pb-3 border-b border-border" data-testid="agent-leads-stage-pills">
            <button
              type="button"
              onClick={() => setStageFilter('all')}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border ${
                stageFilter === 'all'
                  ? 'bg-[hsl(var(--primary))] text-white border-transparent'
                  : 'bg-muted/50 text-foreground border-border hover:bg-muted'
              }`}
              data-testid="agent-stage-pill-all"
            >
              Tutti · {leads.length}
            </button>
            {Object.entries(STAGE_LABELS).map(([key, label]) => {
              const count = leadsByStage[key] || 0;
              if (count === 0) return null;
              const isActive = stageFilter === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStageFilter(isActive ? 'all' : key)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border ${
                    isActive
                      ? 'bg-[hsl(var(--primary))] text-white border-transparent'
                      : 'bg-muted/50 text-foreground border-border hover:bg-muted'
                  }`}
                  data-testid={`agent-stage-pill-${key}`}
                >
                  {label} · {count}
                </button>
              );
            })}
          </div>

          <div className="space-y-1 max-h-[600px] overflow-auto">
            {filteredLeads.length === 0 && (
              <div className="text-sm text-muted-foreground py-6 text-center italic" data-testid="agent-leads-empty">
                {stageFilter === 'all'
                  ? 'Nessun lead nel periodo selezionato.'
                  : `Nessun lead nella fase "${STAGE_LABELS[stageFilter] || stageFilter}".`}
              </div>
            )}
            {(leadsExpanded ? filteredLeads : filteredLeads.slice(0, 20)).map((l) => (
              <Link
                key={l.id}
                to={`/app/leads/${l.id}`}
                data-testid={`agent-lead-${l.id}`}
                className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-[hsl(var(--accent))] transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-[var(--surface-2)] grid place-items-center font-serif text-xs text-[hsl(var(--primary))]">
                  {(l.name || '?').slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm leading-tight truncate">{l.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {STAGE_LABELS[l.stage] || l.stage || '—'} · {SOURCE_LABELS[l.source] || l.source} · {fmtRelative(l.updated_at || l.created_at)}
                  </div>
                </div>
                <ScoreBadge score={l.score} />
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// --------------------------------------------------------------------------
function KpiTile({ label, value, icon: Icon, color = 'emerald', isString = false }) {
  const colorClasses = {
    emerald: 'bg-emerald-100 text-emerald-700',
    cyan:    'bg-cyan-100 text-cyan-700',
    amber:   'bg-amber-100 text-amber-700',
    green:   'bg-green-100 text-green-700',
  };
  return (
    <Card className="border-border">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="font-serif text-2xl mt-1 text-[hsl(var(--primary))]">
              {isString ? value : (value || 0)}
            </div>
          </div>
          <div className={`w-9 h-9 rounded-lg grid place-items-center ${colorClasses[color]}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ConversionCard({ kind, kpi, title, description }) {
  if (!kpi) return null;
  const onTarget = kpi.on_target;
  // ★ 2026-08-02 v2 — KPI Associate ora è Risposta → Show (primary field).
  const pct = kpi.risposta_to_show_pct ?? kpi.lead_to_show_pct ?? kpi.show_to_close_pct ?? 0;
  const target = kpi.target_pct;
  const dataTestId = `conversion-card-${kind}`;
  // ★ 2026-08-02 (Fase 30 F3) — Delta calendar-month (solo Associate)
  const delta = kpi.delta_risposta_to_show_pct;
  const hasDelta = delta !== null && delta !== undefined;

  return (
    <Card
      className={`border-2 ${onTarget ? 'border-emerald-300 bg-emerald-50/40' : 'border-amber-300 bg-amber-50/40'}`}
      data-testid={dataTestId}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
              <Target className="w-3 h-3" /> {title}
            </div>
            <div className="flex items-baseline gap-2 mt-2">
              <div className={`font-serif text-4xl ${onTarget ? 'text-emerald-700' : 'text-amber-700'}`}>
                {pct.toFixed(1)}%
              </div>
              <div className="text-sm text-muted-foreground">/ {target.toFixed(0)}% target</div>
              {hasDelta && (() => {
                const isZero = Math.abs(delta) < 0.05;
                const isPositive = delta > 0;
                const cls = isZero
                  ? 'bg-slate-100 text-slate-600 border-slate-300'
                  : (isPositive
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-amber-50 text-amber-700 border-amber-200');
                const sign = isZero ? '' : (isPositive ? '+' : '');
                return (
                  <span
                    className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md border text-[10px] font-medium ${cls}`}
                    title="Variazione vs mese calendario precedente"
                    data-testid="agent-delta-chip"
                  >
                    {sign}{Number(delta).toFixed(1)}% vs mese scorso
                  </span>
                );
              })()}
            </div>
            <div className="mt-2 text-xs">
              {onTarget ? (
                <span className="inline-flex items-center text-emerald-700 font-medium">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> In target ✓
                </span>
              ) : (
                <span className="inline-flex items-center text-amber-700 font-medium">
                  <AlertCircle className="w-3.5 h-3.5 mr-1" /> Sotto target
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">{description}</p>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-3 h-2.5 rounded-full bg-muted/50 relative overflow-hidden">
          <div
            className={`h-full ${onTarget ? 'bg-emerald-500' : 'bg-amber-500'}`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
          <div
            className="absolute top-0 bottom-0 border-l-2 border-dashed border-rose-500"
            style={{ left: `${Math.min(target, 100)}%` }}
            title={`Target ${target}%`}
          />
        </div>
      </CardContent>
    </Card>
  );
}
